import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ElementRef,
  SimpleChanges,
  OnChanges,
  SimpleChange,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnCurrentOrganization } from '@lib/auth';
import { t } from '@lib/translation';
import { SnActionConfig } from '@lib/ui/actions';
import { SnLayoutService } from '@lib/ui/layout';
import { SentryVaultComponent } from '@lib/vault';

import {
  LprWarningEntry,
  LPR_MESSAGES_TRANSLATION_KEY,
  IFsLprResult,
  FsLprFleetRead,
  SnLprV3FlockRead,
  LprIntegrationType,
  IFsLprProviderInterface,
} from '../../api';
import { FleetLprService } from '../../integrations';
import { PlateReviewService, FsLprReportGeneratorService } from '../../services';
import { QueryResultsMapViewComponent } from '../query-results-map-view/query-results-map-view.component';

@Component({
  selector: 'fs-lpr-view-stages',
  templateUrl: './lpr-view-stages.component.html',
  styleUrls: ['./lpr-view-stages.component.scss'],
})
export class LprViewStagesComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('plateResults')
  private plateResultsRef!: ElementRef<HTMLDivElement>;

  public readonly paginationOptions: number[] = [50, 100, 200];
  public pageLimit: number = this.paginationOptions[0];

  @Input() public isLoading: boolean;
  @Input() public hasLoaded: boolean;
  @Input() public hasBoundingBoxParams: boolean = false;
  @Input() public page: number;
  @Input() public results: IFsLprResult[] = [];
  @Input() public totalResultsCount: {
    total: number;
    totalQueryResults: number;
  };
  @Input() public openLightboxFunction: (imageUrls: string[]) => void;
  @Input() public mode: string = 'cards';
  @Input() public isNextPageDisabled: boolean = false;
  @Input() public errors: string[] = [];
  @Input() public warnings: LprWarningEntry[] = [];
  @Input() public selectedPlate: IFsLprResult | null = null;
  @Input() public getPlateWarningInfoTooltip: (plate: FsLprFleetRead) => string | null;
  @Input() public activeProvider: any = null;
  @Input() public hasProviderWarnings: boolean = false;
  @Input() public areAllProvidersEmpty: boolean = false;
  @Input() public activeProviders: IFsLprProviderInterface[] = [];
  @Input() public shouldShowPartialMatches: boolean = false;
  @Input() public isReviewingPlates: boolean = false;
  @Input() public closedPlateDetails$: Observable<FsLprFleetRead>;

  @Output() public plateClicked = new EventEmitter<IFsLprResult>();
  @Output() public firstPageClicked = new EventEmitter<void>();
  @Output() public previousPageClicked = new EventEmitter<void>();
  @Output() public nextPageClicked = new EventEmitter<void>();
  @Output() public pageLimitChange = new EventEmitter<number>();
  @Output() public cancelSearch = new EventEmitter<void>();
  @Output() public hideDetails = new EventEmitter<boolean>();

  public readonly columns: string[] = this._genColumns();
  public readonly actions: SnActionConfig<IFsLprResult>[] = this._genActions();

  public selectedCardIndex: number = -1;

  public readonly MESSAGES = LPR_MESSAGES_TRANSLATION_KEY;

  get sourceAndDeviceManagementEnabled(): boolean {
    return this._currentOrganization.settings?.sourceAndDeviceManagementEnabled ?? false;
  }

  get hasFlockV3Results(): boolean {
    return (
      this._currentOrganization.enabledIntegrations.includes('flock_ps_v3') &&
      this.results.some(
        (result) =>
          (result as FsLprFleetRead).integratedLprType === LprIntegrationType.FLOCK_V3 &&
          !!(result as IFsLprResult as SnLprV3FlockRead).imageUrl,
      )
    );
  }

  @Input()
  set plate(newPlate: FsLprFleetRead | null) {
    // Reset selected card if passed a null plate
    if (newPlate == null) {
      this.selectedCardIndex = -1;
      this.selectedPlate = null;
      return;
    }

    // Update the selected card if the plate is in the current page of results.
    // If not, reset the selected card.
    const newIndex = this.results.findIndex((plate) => plate.id === newPlate.id);
    if (newIndex >= 0) {
      this.selectedCardIndex = newIndex;
      this.selectedPlate = this.results[this.selectedCardIndex];
      this._cd.detectChanges();
    } else {
      this.selectedCardIndex = -1;
      this.selectedPlate = null;
    }
  }

  private isDialogOpen: boolean = false;
  private _destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly _cd: ChangeDetectorRef,
    private readonly _plateReviewService: PlateReviewService,
    private readonly _layoutService: SnLayoutService,
    private readonly _matDialog: MatDialog,
    private readonly _lprReportGeneratorService: FsLprReportGeneratorService,
    private readonly _lprFleetService: FleetLprService,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {}

  public ngOnInit(): void {
    this._setClosedPlateDetailsSubscription();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._layoutService.appbar.actions.set([]);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedPlate) {
      this._handlePlateSelection(changes.selectedPlate);
    }
  }

  @HostListener('document:keydown', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.mode !== 'cards' || !this.hasLoaded || this.isLoading || this.results.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        this.navigateCards(-1);
        break;
      case 'ArrowRight':
        this.navigateCards(1);
        break;
      case 'Enter':
        break;
    }
  }

  private navigateCards(direction: number): void {
    const newIndex = this.selectedCardIndex + direction;
    if (newIndex >= 0 && newIndex < this.results.length) {
      this.selectedCardIndex = newIndex;
      this.selectedPlate = this.results[this.selectedCardIndex];
      this.plateClicked.emit(this.selectedPlate);
      this._cd.detectChanges();
    }
  }

  public onPlateClick(clickedPlate: FsLprFleetRead): void {
    this.selectedPlate = clickedPlate;
    this.selectedCardIndex = this.results.findIndex((plate) => plate.id === clickedPlate.id);
    this.plateClicked.emit(clickedPlate);
  }

  public focusPlate(event: FocusEvent, index: number): void {
    this.selectedCardIndex = index;
    this._scrollToPlate(event.target as HTMLElement);
  }

  public onPaginationChange(): void {
    this.pageLimitChange.emit(this.pageLimit);
  }

  public firstPage(): void {
    this.firstPageClicked.next();
  }

  public previousPage(): void {
    this.previousPageClicked.next();
  }

  public nextPage(): void {
    this.nextPageClicked.next();
  }

  public scrollToTop(): void {
    if (this.plateResultsRef?.nativeElement) {
      this.plateResultsRef.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  public startAtTop(): void {
    if (this.plateResultsRef?.nativeElement) {
      this.plateResultsRef.nativeElement.scrollTop = 0;
    }
  }

  public cancel(): void {
    this.cancelSearch.next();
  }

  public onHideDetails(hideDetails: boolean): void {
    this.hideDetails.emit(hideDetails);
  }

  public shouldShowNoResultsBanner(): boolean {
    if (!this.sourceAndDeviceManagementEnabled) {
      return false;
    }

    const areActiveProvidersEmpty = this.activeProviders.length === 0;
    const areAnyProvidersLoading = this.activeProviders.some((provider) => provider.isLoading);
    if (areAnyProvidersLoading) {
      return false;
    }

    const areAllProvidersLoadedWithWarningsOrErrors = this.activeProviders.every(
      (provider) =>
        !provider.isLoading &&
        (provider.errors?.length > 0 ||
          (this.shouldShowPartialMatches ? false : provider.warnings?.length > 0)),
    );
    const areAllLoadedProvidersWithNoResults = this.activeProviders.every((provider) => {
      if (provider.isLoading) {
        return false;
      }
      const hasNoRawResults = provider.results?.length === 0;
      const areResultsExcludedByIssues =
        provider.errors?.length > 0 ||
        (!this.shouldShowPartialMatches && provider.warnings?.length > 0);
      return hasNoRawResults || areResultsExcludedByIssues;
    });
    const areResultsEmpty = this.results.length === 0;

    return (
      areActiveProvidersEmpty ||
      areAllProvidersLoadedWithWarningsOrErrors ||
      areAllLoadedProvidersWithNoResults ||
      areResultsEmpty
    );
  }

  private _addToVault(vault: string, formData: any): void {
    this._lprFleetService.addMediaToVault(vault, formData).subscribe();
  }

  private _genColumns(): string[] {
    return ['plate', 'make', 'color', 'created', 'camera', 'actions'];
  }

  private _genActions(): SnActionConfig<FsLprFleetRead>[] {
    return [
      {
        label: t('alpr.actions.sendToVault'),
        icon: {
          icon: 'queue_play_next',
        },
        click: (plate: FsLprFleetRead) => {
          if (this.isDialogOpen) {
            return;
          }
          this.isDialogOpen = true;
          this._lprReportGeneratorService.generatePdfReport(plate).then((blob) => {
            const now = new Date();
            const file = new File([blob], `ALPR_Report_${plate.plate}_${now.getTime()}.pdf`, {
              type: blob.type,
            });
            const formData = new FormData();
            formData.append('files', file, file.name);
            const dialog = this._matDialog.open(SentryVaultComponent);
            dialog
              .afterClosed()
              .pipe(takeUntil(this._destroy$))
              .subscribe((vaultID) => {
                this.isDialogOpen = false;
                const vault = vaultID?.id || vaultID;
                if (vault) {
                  this._addToVault(vault, formData);
                }
              });
          });
        },
      },
      {
        label: t('alpr.actions.markForReview'),
        icon: {
          fontIcon: 'fa-bookmark',
          fontSet: 'fal',
          // TODO: Implement passing data into the class function for ActionConfig so we can check here instead of in the table template
          class: 'markForReviewButton',
        },
        click: (plate: IFsLprResult) => {
          const isMarkedForReview = this._plateReviewService.isMarkedForReview(plate.id);
          if (isMarkedForReview) {
            this._plateReviewService.removeFromReview(plate.id);
          } else {
            this._plateReviewService.markForReview(plate);
          }
        },
      },
      {
        label: t('alpr.actions.showOnMap'),
        icon: 'gps_fixed',
        click: (plate: IFsLprResult) => {
          if (this.isDialogOpen) {
            return;
          }
          this.isDialogOpen = true;
          const mapResults = this._matDialog.open(QueryResultsMapViewComponent, {
            data: plate,
            width: '75vw',
            hasBackdrop: true,
          });
          mapResults
            .afterClosed()
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
              this.isDialogOpen = false;
            });
        },
      },
    ];
  }

  private _handlePlateSelection(change: SimpleChange): void {
    const { currentValue, previousValue } = change;
    if (currentValue?.id !== previousValue?.id) {
      const selectedPlateEl = document.getElementById(`plate-card-${currentValue?.id}`);
      selectedPlateEl?.focus();
      this._scrollToPlate(selectedPlateEl);
    }
  }

  get isPreviousPageDisabled(): boolean {
    return this.page === 1;
  }

  private _setClosedPlateDetailsSubscription(): void {
    this.closedPlateDetails$
      .pipe(
        takeUntil(this._destroy$),
        tap((closedPlate) => {
          const closedPlateHtmlElement = document.getElementById(`plate-card-${closedPlate.id}`);
          this._scrollToPlate(closedPlateHtmlElement);
        }),
      )
      .subscribe();
  }

  private _scrollToPlate(plateHtmlElement: HTMLElement): void {
    if (!plateHtmlElement) {
      return;
    }

    // Small delay so the scroll doesn't clash with the browser's own scroll handling on resize
    const browserScrollHandleTimeoutMs = 100;
    const scrollOptions: ScrollIntoViewOptions = { behavior: 'smooth', block: 'nearest' };

    setTimeout(() => {
      plateHtmlElement.scrollIntoView(scrollOptions);
    }, browserScrollHandleTimeoutMs);
  }
}
